import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import i18n from "@/i18n";
import { en } from "@/i18n/en";
import { fr } from "@/i18n/fr";
import { nl } from "@/i18n/nl";
import { buildHead } from "@/lib/seo";
import { HomePage } from "@/components/pages/HomePage";
import { ServicesPage } from "@/components/pages/ServicesPage";
import { ReferencesPage } from "@/components/pages/ReferencesPage";
import { AboutPage } from "@/components/pages/AboutPage";
import { FaqPage } from "@/components/pages/FaqPage";
import { ToolsPage } from "@/components/pages/ToolsPage";
import { ContactPage } from "@/components/pages/ContactPage";
import type { Lang } from "@/i18n";
import { SITE, alternates, urlFor } from "@/lib/site";

const dicts = { fr, en, nl } as const;

type PageKey = "home" | "services" | "references" | "about" | "faq" | "tools" | "contact";

function dictPage(lang: Lang, key: Exclude<PageKey, "home" | "tools">) {
  return dicts[lang][key];
}

export function makeRoute(routePath: string, lang: Lang, key: PageKey) {
  const dict = dicts[lang];
  let titleDesc: { title: string; description: string };
  if (key === "home") {
    titleDesc = { title: dict.home.title, description: dict.home.description };
  } else if (key === "tools") {
    titleDesc = { title: dict.tools.title, description: dict.tools.description };
  } else {
    titleDesc = { title: dictPage(lang, key).title, description: dictPage(lang, key).description };
  }

  const extraLd: Array<Record<string, unknown>> = [];
  if (key === "faq") {
    extraLd.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: dict.faq.items.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      })),
    });
    if (dict.faq.glossary && dict.faq.glossary.length > 0) {
      extraLd.push({
        "@context": "https://schema.org",
        "@type": "DefinedTermSet",
        name: dict.faq.glossaryTitle,
        hasDefinedTerm: dict.faq.glossary.map((g) => ({
          "@type": "DefinedTerm",
          name: g.term,
          description: g.def,
          inDefinedTermSet: urlFor(lang, "faq"),
        })),
      });
    }
  }
  if (key === "about") {
    extraLd.push({
      "@context": "https://schema.org",
      "@type": "Person",
      name: SITE.founder,
      jobTitle: "Founder, hospitality consultant",
      worksFor: { "@type": "Organization", name: SITE.name },
      sameAs: [SITE.linkedin],
      knowsAbout: [
        "Hospitality operations",
        "Hotel pre-opening",
        "Hotel opening",
        "Hotel management",
        "Hotel PMS",
        "Revenue management",
        "Hotel permits Belgium",
        "Short-stay operations",
        "Hybrid hospitality",
      ],
      alumniOf: {
        "@type": "EducationalOrganization",
        name: "Haute École de la Province de Namur — ISGH (Institut Supérieur de Gestion Hôtelière)",
        sameAs: "https://www.hepn.be/",
      },
    });
  }
  if (key === "services") {
    dict.services.items.forEach((s) => {
      extraLd.push({
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: s.title,
        provider: { "@type": "Organization", name: SITE.name, url: SITE.url },
        areaServed: ["Belgium", "Luxembourg"],
        description: s.intro,
      });
    });
    extraLd.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: dict.services.miniFaq.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      })),
    });
  }

  // BreadcrumbList for all non-home pages
  if (key !== "home") {
    const navLabels: Record<Exclude<PageKey, "home">, string> = {
      services: dict.nav.services,
      references: dict.nav.references,
      about: dict.nav.about,
      faq: dict.nav.faq,
      tools: dict.nav.tools,
      contact: dict.nav.contact,
    };
    extraLd.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: dict.breadcrumb?.home ?? "Home",
          item: urlFor(lang, "home"),
        },
        {
          "@type": "ListItem",
          position: 2,
          name: navLabels[key],
          item: urlFor(lang, key),
        },
      ],
    });
  }

  return createFileRoute(routePath as never)({
    head: () =>
      buildHead({
        lang,
        page: key,
        title: titleDesc.title,
        description: titleDesc.description,
        jsonLd: extraLd,
      }),
    component: () => <PageRenderer lang={lang} pageKey={key} />,
  });
}

function PageRenderer({ lang, pageKey }: { lang: Lang; pageKey: PageKey }) {
  useEffect(() => {
    if (i18n.language !== lang) i18n.changeLanguage(lang);
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  // Force first-render with right language: changeLanguage is async but resources are sync
  if (i18n.language !== lang) i18n.changeLanguage(lang);

  switch (pageKey) {
    case "home":
      return <HomePage lang={lang} />;
    case "services":
      return <ServicesPage lang={lang} />;
    case "references":
      return <ReferencesPage lang={lang} />;
    case "about":
      return <AboutPage />;
    case "faq":
      return <FaqPage lang={lang} />;
    case "tools":
      return <ToolsPage lang={lang} />;
    case "contact":
      return <ContactPage lang={lang} />;
  }
}

// Suppress unused import warning for alternates (used elsewhere)
export const _ = alternates;
